import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';

import ScheduleBuilderSession from 'features/scheduleBuilder/ScheduleBuilderView/resources/ScheduleBuilderSession';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';
import type { SessionFetchActionForLocations } from '../types';

import { SLICE_NAME } from './constants';
import type {
  Location,
  LocationSerializedEntity,
  LocationsState,
} from './types';

export const locationsAdapter = createEntityAdapter<Location>();

export const initialState: LocationsState = {
  ...locationsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

/** example location payload object
 * {
            "id": "1",
            "type": "location",
            "attributes": {
                "name": "Homebase",
                "timeZone": "UTC",
                "startOfWeek": 1,
                "openTime": 9,
                "closingTime": 17,
                "enabledWeeklyOvertime": true,
                "enabledDailyOvertime": true,
                "weeklyOvertime": 40,
                "dailyOvertime": 8,
                "isHolidaySettingEnabled": false
            },
            "relationships": {
                "timeOffBlackoutPeriods": {
                    "data": []
                }
            }
        },
 */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: locationsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(
      ScheduleBuilderSession.resourceActionEvent('fetch'),
      (
        state,
        action: SessionFetchActionForLocations<LocationSerializedEntity>
      ) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const locations = action?.payload?.included
              .filter(
                (entity: LocationSerializedEntity) => entity.type === 'location'
              )
              .map((entity: LocationSerializedEntity) => ({
                id: entity.id,
                type: entity.type,
                closingTime: entity.attributes.closingTime,
                dailyOvertime: entity.attributes.dailyOvertime,
                enabledDailyOvertime: entity.attributes.enabledDailyOvertime,
                enabledWeeklyOvertime: entity.attributes.enabledWeeklyOvertime,
                isHolidaySettingEnabled:
                  entity.attributes.isHolidaySettingEnabled,
                name: entity.attributes.name,
                openTime: entity.attributes.openTime,
                startOfWeek: entity.attributes.startOfWeek,
                timeZone: entity.attributes.timeZone,
                weeklyOvertime: entity.attributes.weeklyOvertime,
                timeOffBlackoutPeriods:
                  entity.relationships?.timeOffBlackoutPeriods?.data,
              }));
            locationsAdapter.setAll(state, locations);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const {
  selectById: selectLocationById,
  selectIds: selectLocationIds,
  selectEntities: selectLocationEntities,
  selectAll: selectAllLocations,
  selectTotal: selectTotalLocations,
} = locationsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const { reducer, actions } = slice;

export const selectLocationAttribute = <T extends keyof Location>({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: T;
}) =>
  selectAttributeForEntity({
    state,
    attribute,
    entitySelector: selectLocationById,
    id,
  });
